import { createApi } from '@reduxjs/toolkit/query/react';
import baseApi from './base';
import axios from 'axios';
import streamApi from 'services/stream';

import { envConfig } from 'config';
import paginationParams from 'utils/pagination-params';

export const list = 'LIST_PROMPTS';
export const single = 'SINGLE_PROMPT';

export const promptsApi = createApi({
  reducerPath: 'promptsApi',
  baseQuery: baseApi,
  tagTypes: [list, single],
  endpoints: (builder) => ({
    uploadFile: builder.mutation({
      queryFn: async (args) => {
        const response = await axios.post(`${envConfig.apiBaseUrl}/generate-presigned-upload-url`, {
          s3Key: args.fileName
        });

        const uploadResponse = await axios.put(response?.data?.data?.presignedUrl, args.file, {
          headers: {
            'Content-Type': args.fileType
          },
          ...(args.onProgress && {
            onUploadProgress: (progressEvent) => {
              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              args.onProgress(progress);
            }
          })
        });
        return uploadResponse;
      }
    }),
    generatePrompt: builder.mutation({
      query: (args) =>
        streamApi({
          url: '/generate-prompt',
          method: 'POST',
          body: { definition: args.definition },
          onStream: args.onStream
        })
    }),
    testPrompt: builder.mutation({
      query: (args) =>
        streamApi({
          url: '/test-prompt',
          method: 'POST',
          body: {
            s3Key: args.s3Key,
            prompt: args.prompt
          },
          onStream: args.onStream
        })
    }),
    createPrompt: builder.mutation({
      query: (prompt) => ({
        url: '/prompts/create',
        method: 'POST',
        body: prompt
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            promptsApi.util.updateQueryData('listPrompts', undefined, (draft) => {
              draft.data.items.unshift(created.data);
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    updatePrompt: builder.mutation({
      query: ({ promptId, ...attributes }) => ({
        url: `/prompts/update/${promptId}`,
        method: 'POST',
        body: { ...attributes }
      }),
      invalidatesTags: [list, single]
    }),
    deletePrompt: builder.mutation({
      query: (promptId) => ({
        url: `/prompts/delete/${promptId}`,
        method: 'DELETE'
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            promptsApi.util.updateQueryData('listPrompts', undefined, (draft) => {
              const idx = draft.data.items.findIndex((item) => item.id === id);
              if (idx > -1) {
                draft.data.items.splice(idx, 1);
              }
            })
          );
        } catch (err) {
          console.error('Error updating cache:', err);
        }
      }
    }),
    getPromptById: builder.query({
      query: (promptId) => `/prompts/get/${promptId}`,
      providesTags: [single]
    }),
    listPrompts: builder.query({
      query: (params) => ({
        url: '/prompts/list',
        params
      }),
      providesTags: [list],
      ...paginationParams
    }),
    savePromptVersion: builder.mutation({
      query: ({ promptId, ...attributes }) => ({
        url: `/prompts/versions/save`,
        method: 'POST',
        body: { promptId, ...attributes }
      }),
      invalidatesTags: [single]
    }),
    markAsCurrentVersion: builder.mutation({
      query: ({ promptId, versionId }) => ({
        url: `/prompts/versions/mark-as-current`,
        method: 'POST',
        body: { promptId, id: versionId }
      }),
      invalidatesTags: [single]
    }),
    deletePromptVersion: builder.mutation({
      query: ({ promptId, versionId }) => ({
        url: `/prompts/versions/delete/${promptId}/${versionId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [single]
    })
  })
});

export const {
  useUploadFileMutation,
  useGeneratePromptMutation,
  useTestPromptMutation,
  useCreatePromptMutation,
  useUpdatePromptMutation,
  useDeletePromptMutation,
  useGetPromptByIdQuery,
  useListPromptsQuery,
  useSavePromptVersionMutation,
  useMarkAsCurrentVersionMutation,
  useDeletePromptVersionMutation
} = promptsApi;
